import linearReport from 'c-components/HiddenReport/Custom/configs/linearReport.json';
import dspReport from 'c-components/HiddenReport/Custom/configs/dspReport.json';
import talonLinearReport from 'c-components/HiddenReport/Custom/configs/talonLinearReport.json';
import showcaseReport from 'c-components/HiddenReport/Custom/configs/showcaseReport.json';
import type {
    ChartSpec,
    CustomGroupByField,
    CustomReportConfig,
    CustomReportSlideConfig,
} from 'c-components/HiddenReport/Custom/customReportTypes';
import type { Payload as HiddenReportPayload } from 'c-components/HiddenReport/HiddenReport';

/**
 * The PCA customisation dialog's config editing rules: base configs, new slides, chart grouping,
 * presets and labels. Moved out of PCACustomizationDialog.tsx so they can be tested.
 */

export type BaseConfigKey = 'linear' | 'dsp' | 'talonLinear' | 'showcase';

const baseConfigMap: Record<BaseConfigKey, CustomReportConfig> = {
    linear: linearReport as CustomReportConfig,
    dsp: dspReport as CustomReportConfig,
    talonLinear: talonLinearReport as CustomReportConfig,
    showcase: showcaseReport as CustomReportConfig,
};

export const baseConfigOptions: { value: BaseConfigKey; label: string }[] = [
    { value: 'linear', label: 'Linear' },
    { value: 'dsp', label: 'DSP' },
    { value: 'talonLinear', label: 'Talon Linear' },
    { value: 'showcase', label: 'Showcase' },
];

export const cloneConfig = (baseKey: BaseConfigKey): CustomReportConfig =>
    JSON.parse(JSON.stringify(baseConfigMap[baseKey])) as CustomReportConfig;

export const createSlideId = (layout: CustomReportSlideConfig['layout']) =>
    `${layout}-${Math.random().toString(36).slice(2, 8)}`;

export const createEmptySlide = (
    layout: CustomReportSlideConfig['layout'],
): CustomReportSlideConfig => {
    const id = createSlideId(layout);

    switch (layout) {
        case 'summaryCover':
            return {
                id,
                enabled: true,
                layout,
                preset: 'linear',
                title: 'Campaign Summary',
                showPerformanceHeadlines: true,
                showCta: false,
            };
        case 'linearTwoChart':
            return {
                id,
                enabled: true,
                layout,
                mainTitle: 'New Two Chart Slide',
                left: {
                    dataset: 'display_owner',
                    groupByFields: ['display_owner'],
                    metric: 'impacts',
                    title: 'Left Chart',
                    chartKind: 'bar',
                },
                right: {
                    dataset: 'display_owner',
                    groupByFields: ['display_owner'],
                    metric: 'plays',
                    title: 'Right Chart',
                    chartKind: 'bar',
                },
            };
        case 'singleChart':
            return {
                id,
                enabled: true,
                layout,
                mainTitle: 'New Single Chart Slide',
                chart: {
                    dataset: 'display_owner',
                    groupByFields: ['display_owner'],
                    metric: 'impacts',
                    title: 'Chart',
                    chartKind: 'bar',
                },
            };
        case 'dspSplitChartInsights':
            return {
                id,
                enabled: true,
                layout,
                heading: 'New Delivery & Insights Slide',
                chart: {
                    dataset: 'display_owner',
                    groupByFields: ['display_owner'],
                    metric: 'impacts',
                    title: 'Total Impacts',
                    chartKind: 'bar',
                },
                insights: {
                    kind: 'shareBreakdown',
                    dataset: 'display_owner',
                    metric: 'impacts',
                },
            };
        case 'performanceBoxes':
            return {
                id,
                enabled: true,
                layout,
                mainTitle: 'New Performance Boxes Slide',
                title: 'Performance Boxes',
                dataset: 'display_owner',
                metric: 'impacts',
                columns: 3,
                sortBy: 'performance',
            };
        case 'impactMultiplier':
            return {
                id,
                enabled: true,
                layout,
                preset: 'maintainGrow',
            };
        case 'oneMap':
            return {
                id,
                enabled: true,
                layout,
                mainTitle: 'New Map Slide',
                map: {
                    title: 'Impacts Heatmap',
                    mode: 'heat',
                },
            };
        case 'twoMap':
            return {
                id,
                enabled: true,
                layout,
                mainTitle: 'New Two Map Slide',
                left: {
                    title: 'Left Map',
                    mode: 'heat',
                },
                right: {
                    title: 'Right Map',
                    mode: 'markers',
                },
            };
        case 'glossary':
            return {
                id,
                enabled: true,
                layout,
                preset: 'linear',
                title: 'Glossary',
            };
        case 'thankYou':
            return {
                id,
                enabled: true,
                layout,
                preset: 'linear',
                title: 'Thank You',
            };
        default:
            return {
                id,
                enabled: true,
                layout: 'summaryCover',
                preset: 'linear',
                title: 'Campaign Summary',
            };
    }
};

export const toLabel = (value: string) =>
    value
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, letter => letter.toUpperCase());

// Label for an option dropdown value. Prefers a translation under
// PcaCustomization.optionLabels, falling back to the generic humaniser when the value has
// no translation (e.g. slide layouts / presets that keep their raw labels).
export const OPTION_LABEL_NS = 'Modules.Main.Campaigns.Overview.PcaCustomization.optionLabels';
export const optionLabel = (value: string, t: (key: string) => string): string => {
    const key = `${OPTION_LABEL_NS}.${value}`;
    const translated = t(key);
    return !translated || translated === key || translated.endsWith(`.${value}`)
        ? toLabel(value)
        : translated;
};

export const getCurrentReportEnvironment = (): HiddenReportPayload['environment'] => {
    const host = window.location.hostname.toLowerCase();

    if (host.includes('localhost') || host.includes('127.0.0.1')) return 'local';
    if (host.includes('staging')) return 'staging';

    return 'production';
};

export const getSlideLabel = (slide: CustomReportSlideConfig) => {
    if ('mainTitle' in slide) return slide.mainTitle;
    if ('heading' in slide) return slide.heading;
    if ('title' in slide && slide.title) return slide.title;
    return toLabel(slide.layout);
};

export const isRunningTotalsChart = (chart: ChartSpec) =>
    chart.dataset === 'running_totals_impacts' || chart.dataset === 'running_totals_plays';

export const getChartGroupByFields = (chart: ChartSpec): CustomGroupByField[] => {
    if (chart.groupByFields?.length) return chart.groupByFields;
    if (chart.dataset === 'display_env_format') return ['display_env', 'display_format'];
    if (
        chart.dataset === 'display_owner' ||
        chart.dataset === 'display_env' ||
        chart.dataset === 'display_format' ||
        chart.dataset === 'display_tv_region' ||
        chart.dataset === 'day_of_week' ||
        chart.dataset === 'hour_of_day'
    ) {
        return [chart.dataset];
    }
    return [];
};

export const setChartGroupByFields = (
    chart: ChartSpec,
    nextFields: CustomGroupByField[],
): ChartSpec => {
    if (!nextFields.length) return chart;

    const dataset =
        nextFields.length === 1
            ? nextFields[0]
            : nextFields[0] === 'display_env' && nextFields[1] === 'display_format'
            ? 'display_env_format'
            : chart.dataset;

    return {
        ...chart,
        dataset,
        groupByFields: nextFields,
    };
};

export const getPresetOptions = (slide: CustomReportSlideConfig): string[] => {
    switch (slide.layout) {
        case 'summaryCover':
            return ['linear', 'dspSummary', 'dspCover'];
        case 'impactMultiplier':
            return ['maintainGrow', 'reviewConsider'];
        case 'glossary':
            return ['linear', 'dspCover', 'dspIntro', 'dspTerms'];
        case 'thankYou':
            return ['linear', 'dsp'];
        default:
            return [];
    }
};

export const getPresetValue = (slide: CustomReportSlideConfig) =>
    'preset' in slide ? slide.preset ?? slide.variant ?? '' : '';

export const replaceSlideLayout = (
    slide: CustomReportSlideConfig,
    nextLayout: CustomReportSlideConfig['layout'],
): CustomReportSlideConfig => {
    const nextSlide = createEmptySlide(nextLayout);

    return {
        ...nextSlide,
        id: slide.id,
        enabled: slide.enabled,
        layoutConfig: slide.layoutConfig,
    };
};

export const toggleStringArrayValue = <T extends string>(
    items: T[] | undefined,
    value: T,
    checked: boolean,
) =>
    checked
        ? Array.from(new Set([...(items ?? []), value]))
        : (items ?? []).filter(item => item !== value);

/** After removing a slide, select the one that took its place, else the one before, else none. */
export const removeSlide = (slides: CustomReportSlideConfig[], removedId: string) => {
    const currentIndex = slides.findIndex(slide => slide.id === removedId);
    const nextSlides = slides.filter(slide => slide.id !== removedId);
    const nextSelected =
        nextSlides[currentIndex] ?? nextSlides[currentIndex - 1] ?? nextSlides[0] ?? null;
    return { slides: nextSlides, selectedId: nextSelected?.id ?? null };
};

/** The hidden-report payload a preview opens with. */
export const previewReportPayload = ({
    campaignId,
    previewSkinId,
    selectedSkinId,
    email,
    britishEnglish,
    environment,
}: {
    campaignId: number;
    previewSkinId: number;
    selectedSkinId: number | null | undefined;
    email: string | null | undefined;
    britishEnglish: boolean;
    environment: HiddenReportPayload['environment'];
}): HiddenReportPayload => ({
    campaign_id: campaignId,
    jwt: '',
    powerpoint_template: 'custom',
    skin_id: previewSkinId || selectedSkinId || 0,
    environment,
    email: email ?? '',
    language: britishEnglish ? 'GB' : 'US',
    third_color: 'false',
});
